import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Label } from '@/components/ui/label';

interface SubscriptionTier {
  id: string;
  tier_name: string;
  stripe_product_id: string;
  stripe_price_id: string;
  price_amount: number;
  description: string;
  features: string[] | any;
  included_categories: string[] | any;
  is_active: boolean;
}

export default function SubscriptionTiersManager() {
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState<SubscriptionTier | null>(null);

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_tiers')
        .select('*')
        .order('price_amount', { ascending: true });

      if (error) throw error;
      
      // Ensure features and included_categories are arrays
      const normalizedTiers = (data || []).map(tier => ({
        ...tier,
        features: Array.isArray(tier.features) ? tier.features : [],
        included_categories: Array.isArray(tier.included_categories) ? tier.included_categories : [],
      }));
      
      setTiers(normalizedTiers);
    } catch (error) {
      console.error('Error loading tiers:', error);
      toast.error('Failed to load subscription tiers');
    } finally {
      setLoading(false);
    }
  };

  const saveTier = async (tier: Partial<SubscriptionTier>) => {
    try {
      const { error } = await supabase
        .from('subscription_tiers')
        .update({
          description: tier.description,
          features: tier.features,
          included_categories: tier.included_categories,
          is_active: tier.is_active,
        })
        .eq('id', tier.id);

      if (error) throw error;
      
      toast.success('Tier updated successfully');
      setEditingTier(null);
      loadTiers();
    } catch (error) {
      console.error('Error saving tier:', error);
      toast.error('Failed to save tier');
    }
  };

  const toggleActive = async (tierId: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('subscription_tiers')
        .update({ is_active: !isActive })
        .eq('id', tierId);

      if (error) throw error;
      
      toast.success('Tier status updated');
      loadTiers();
    } catch (error) {
      console.error('Error toggling tier:', error);
      toast.error('Failed to update tier status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-foreground">Subscription Tiers</h2>
      </div>

      <div className="grid gap-6">
        {tiers.map((tier) => (
          <Card key={tier.id} className="p-6">
            {editingTier?.id === tier.id ? (
              <TierEditor
                tier={editingTier}
                onChange={setEditingTier}
                onSave={() => saveTier(editingTier)}
                onCancel={() => setEditingTier(null)}
              />
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <h3 className="text-xl font-semibold text-foreground">{tier.tier_name}</h3>
                      <span className="text-2xl font-bold text-primary">
                        ${(tier.price_amount / 100).toFixed(2)}/mo
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                    <div className="text-xs text-muted-foreground">
                      <div>Product ID: {tier.stripe_product_id}</div>
                      <div>Price ID: {tier.stripe_price_id}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={tier.is_active}
                      onCheckedChange={() => toggleActive(tier.id, tier.is_active)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingTier(tier)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Features:</h4>
                  <ul className="space-y-1">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground">✓ {feature}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-foreground mb-2">Included Categories:</h4>
                  <div className="flex flex-wrap gap-2">
                    {tier.included_categories.map((cat, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 text-xs rounded-full bg-primary/10 text-primary"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

interface TierEditorProps {
  tier: SubscriptionTier;
  onChange: (tier: SubscriptionTier) => void;
  onSave: () => void;
  onCancel: () => void;
}

function TierEditor({ tier, onChange, onSave, onCancel }: TierEditorProps) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={tier.description}
          onChange={(e) => onChange({ ...tier, description: e.target.value })}
          rows={2}
        />
      </div>

      <div>
        <Label htmlFor="features">Features (one per line)</Label>
        <Textarea
          id="features"
          value={tier.features.join('\n')}
          onChange={(e) =>
            onChange({ ...tier, features: e.target.value.split('\n').filter(Boolean) })
          }
          rows={6}
        />
      </div>

      <div>
        <Label htmlFor="categories">Included Categories (comma-separated)</Label>
        <Input
          id="categories"
          value={tier.included_categories.join(', ')}
          onChange={(e) =>
            onChange({
              ...tier,
              included_categories: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
            })
          }
        />
      </div>

      <div className="flex items-center gap-2">
        <Switch
          checked={tier.is_active}
          onCheckedChange={(checked) => onChange({ ...tier, is_active: checked })}
        />
        <Label>Active</Label>
      </div>

      <div className="flex gap-2">
        <Button onClick={onSave}>
          <Save className="h-4 w-4 mr-2" />
          Save
        </Button>
        <Button variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );
}
